import React, { FC, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AppContext } from "Context/AppContext";
import { toast } from "react-toastify";
import AuthManager from "Common/AuthManager";
import NoDataComponent from "Components/Common/NoDataComponent";
import ComputedSubstitutionTable from "../components/CSTable";
import CSConfigHeader from "../components/CSConfigHeader";
import { CREATE } from "Pages/GMS/ISR/ISRConstants";
import CSSolidLineComponent from "../components/CSSolidLineComponent";
import { deletePivotData, fetchPivotTableData, updateDefaultVersion } from "../services/CSServices";
import { CSContext } from "../contexts/CSContext";
import usePagination from "Components/Common/hooks/usePagination";
import CSTablePagination from "../components/CSTablePagination";
import { ArsContext } from "Pages/GMS/ARS/Contexts/ArsContext";
import { updateRecommenderModel } from "Pages/GMS/ARS/ARSServices/ARSServices";
import ConfirmationModal from "../components/ConfirmationModal";

const ComputedSubstitutionComponent: FC<ComputedSubstitutionComponentProps> = ({ isSidePanel, baseCategories }) => {
  let appContext = useContext(AppContext);
  let industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
  let channelKey = appContext?.channelKey;
  const appData = useContext(AppContext);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const { newlyCreatedId } = useContext(CSContext)
  const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination()
  const { model, setModel, activeSubmodelIndex } = useContext(ArsContext);
  const [defaultSelected, setDefaultSelected] = useState<PivotVersion | null>(null);
  const [removeDefaultId, setRemoveDefaultId] = useState<string | null>(null);
  const [data, setData] = useState<PivotAttributesResponse>({
    data: {
      pivot_attributes: [],
      page: 1,
      per_page: 10,
      total: 0
    },
    done: false
  });

  useEffect(() => {
    if (!appData?.pm?.checkPermission("computedsubstitution_read")) {
      history.push("/workspace/module/permission/denied");
      return;
    }
    if (industryId) { fetchTableData(); }
  }, [industryId, paginationParams, channelKey]);

  const fetchTableData = async () => {
    if (industryId === null) return;
    setLoading(true);
    let res = await fetchPivotTableData(industryId!, channelKey, paginationParams.page, paginationParams.per_page, baseCategories);
    setData(res);
    updateTotalRecords(res?.data?.total)
    setLoading(false);
  };

  const handleDelete = async (selectIndex: SelectIndex) => {
    if (!appData?.pm?.checkPermission("computedsubstitution_delete")) {
      toast.info("You dont have permission for this operation")
      return
    }
    if (selectIndex.defaultIndex === null) return
    const selectedItem: PivotBaseItem = selectIndex.versionIndex !== null && selectIndex.defaultIndex != null ? data.data.pivot_attributes[selectIndex.defaultIndex].versions[selectIndex.versionIndex] : data.data.pivot_attributes[selectIndex.defaultIndex!];
    const permissionStatus = hasPermission(selectedItem, 'delete');
    if (permissionStatus !== true && permissionStatus) {
      showNoPermissionToast(permissionStatus);
      return;
    }
    setLoading(true)
    await deletePivotData(selectedItem.uid)
    fetchTableData()
  }

  const hasPermission = (item: PivotBaseItem, action: 'delete' | 'edit'): boolean | string => {
    if (!AuthManager.isAuthenticated()) return false;

    const userobj = AuthManager.getAuthUser();
    const userProfile = userobj?.profile;

    if (!userProfile) return false;

    const userid = userProfile.id;

    if (userid !== item.created_by.userid) {
      return action === 'delete'
        ? 'You can only delete pivots that you have created'
        : 'You can only edit pivots that you have created';
    }

    if (action === 'delete') {
      if (item.status.toLowerCase() === 'live') {
        return 'Cannot delete default and live pivot attributes';
      }
    } else if (action === 'edit') {
      if (item.status.toLowerCase() === 'live') {
        return 'Cannot edit live pivot attributes';
      }
    }

    return true;
  }

  const showNoPermissionToast = (message?: string) => {
    toast.error(message ?? "You dont have permission for this operation")
  }

  const handleDefaultVersionSelection = async () => {
    if (!industryId || !defaultSelected) return;
    setLoading(true);

    if (isSidePanel) {
      const submodel = model?.submodel[activeSubmodelIndex];

      if (submodel) {
        const updatedPivots = [...submodel.pivots];

        if (removeDefaultId) {
          const pivotIndex = updatedPivots.indexOf(removeDefaultId);
          if (pivotIndex !== -1) {
            updatedPivots.splice(pivotIndex, 1);
          }
        }

        if (defaultSelected && !updatedPivots.includes(defaultSelected.uid)) {
          updatedPivots.push(defaultSelected.uid);
        }

        const updatedSubmodels = model.submodel.map((sm, index) =>
          index === activeSubmodelIndex
            ? { ...sm, pivots: updatedPivots }
            : sm
        );

        const updatedModel = {
          ...model,
          submodel: updatedSubmodels
        };

        setModel(updatedModel);
        const hasValidTitles = model?.submodel?.every(x =>
          x.setModePriority.every(mode => mode.title?.trim().length > 0)
        );

        if (!hasValidTitles) {
          toast.error("All mode titles are required. Please ensure each mode has a valid title.");
          return;
        }
        await updateRecommenderModel(updatedModel);
      }
    } else {
      await updateDefaultVersion(defaultSelected.uid);
    }

    setDefaultSelected(null);
    setRemoveDefaultId(null);
    fetchTableData();
  };

  const handleEditAndCreateVersion = (selectIndex: SelectIndex, type: string) => {
    if (!appData?.pm?.checkPermission("computedsubstitution_create") || !appData?.pm?.checkPermission("computedsubstitution_update")) {
      toast.info("You dont have permission for this operation")
      return
    }
    const selectedItem: PivotBaseItem = selectIndex.versionIndex !== null && selectIndex.defaultIndex != null ? data.data.pivot_attributes[selectIndex.defaultIndex].versions[selectIndex.versionIndex] : data.data.pivot_attributes[selectIndex.defaultIndex!];
    if (type === 'edit' || type === 'edit_version') {
      const permissionStatus = hasPermission(selectedItem, 'edit');
      if (permissionStatus !== true && permissionStatus) {
        showNoPermissionToast(permissionStatus);
        return;
      }
    }
    history.push(`/gms/computed_substitutions/${type}/${selectedItem?.category_id}/${selectedItem?.uid}`);
  };

  return (
    <>
      {defaultSelected !== null && <ConfirmationModal title={"Change default?"} message={`Confirm your choice to set ${defaultSelected.version_name} as the Default`} showModal={defaultSelected !== null} closeModal={() => setDefaultSelected(null)} onConfirm={() => handleDefaultVersionSelection()}></ConfirmationModal >}
      {/* {!isSidePanel && <CSConfigHeader onCLickFunction={handleCreate} linkTo="/gms/computed_substitutions" label="Computed Substitutions" buttonName={CREATE} showBackButton={false} />} */}
      {/* <CSSolidLineComponent /> */}
      <div className="tw-w-[100%] tw-mb-12 tw-">
        {!loading && data?.done === false ? <NoDataComponent /> : <ComputedSubstitutionTable
          pivotData={data}
          setPivotData={setData}
          loading={loading}
          handleDelete={handleDelete}
          handleEditAndCreateVersion={handleEditAndCreateVersion}
          NoDataComponent={NoDataComponent}
          handleDefaultVersionSelection={setDefaultSelected}
          setRemoveDefaultId={setRemoveDefaultId}
          isSidePanel={isSidePanel}
          newlyCreatedId={newlyCreatedId}
        />}
        {!loading && data?.data?.pivot_attributes && data?.data?.pivot_attributes?.length > 0 ?
          <CSTablePagination
            paginationParams={paginationParams}
            totalRecords={totalRecords}
            updatePageSize={updatePageSize}
            handlePageChange={handlePageChange} />
          :
          null
        }
      </div>
    </>
  );
};

export default ComputedSubstitutionComponent;

